/**
 * Annual review mission steps — mirrors ani_api Meeting to Short Letter template
 * (`MISSION_TEMPLATE_MEETING_TO_SHORT_LETTER` in seed_data.py).
 */

export type AnnualReviewMissionStepKey =
  | "capture_meeting"
  | "transcribe_meeting"
  | "approve_transcript"
  | "review_extractions"
  | "draft_handover_notes"
  | "draft_short_letter"
  | "propose_crm_updates";

export type MissionStepOwnerRole = "adviser" | "paraplanner" | "administrator" | "agent";

export type AnnualReviewMissionStep = {
  key: AnnualReviewMissionStepKey;
  stageKey: string;
  /** Short column header for the client matrix. */
  label: string;
  /** Full catalogue name for filters and tooltips. */
  fullLabel: string;
  ownerRole: MissionStepOwnerRole;
};

/** Pipeline order from ani_api `task_graph`. */
export const ANNUAL_REVIEW_MISSION_STEPS: AnnualReviewMissionStep[] = [
  {
    key: "capture_meeting",
    stageKey: "intake",
    label: "Meeting",
    fullLabel: "Capture Meeting Outcome",
    ownerRole: "adviser",
  },
  {
    key: "transcribe_meeting",
    stageKey: "transcription",
    label: "Transcribe",
    fullLabel: "Transcribe Meeting",
    ownerRole: "agent",
  },
  {
    key: "approve_transcript",
    stageKey: "review",
    label: "Transcript",
    fullLabel: "Approve Transcript",
    ownerRole: "adviser",
  },
  {
    key: "review_extractions",
    stageKey: "extraction",
    label: "Extractions",
    fullLabel: "Review Meeting Extractions",
    ownerRole: "adviser",
  },
  {
    key: "draft_handover_notes",
    stageKey: "handover",
    label: "Handover",
    fullLabel: "Draft Handover Notes",
    ownerRole: "paraplanner",
  },
  {
    key: "draft_short_letter",
    stageKey: "draft",
    label: "Letter",
    fullLabel: "Draft Short Letter",
    ownerRole: "paraplanner",
  },
  {
    key: "propose_crm_updates",
    stageKey: "crm_push",
    label: "CRM",
    fullLabel: "Propose CRM Updates",
    ownerRole: "administrator",
  },
];

const STEP_BY_KEY = Object.fromEntries(
  ANNUAL_REVIEW_MISSION_STEPS.map((step) => [step.key, step]),
) as Record<AnnualReviewMissionStepKey, AnnualReviewMissionStep>;

export function isAnnualReviewMissionStepKey(
  key: string,
): key is AnnualReviewMissionStepKey {
  return key in STEP_BY_KEY;
}

export function tryGetMissionStep(key: string): AnnualReviewMissionStep | undefined {
  return isAnnualReviewMissionStepKey(key) ? STEP_BY_KEY[key] : undefined;
}

export function getMissionStep(key: AnnualReviewMissionStepKey): AnnualReviewMissionStep {
  return STEP_BY_KEY[key];
}

export function getMissionStepLabel(key: AnnualReviewMissionStepKey): string {
  return STEP_BY_KEY[key].fullLabel;
}

/** Human label for API step keys that may fall outside the annual-review template. */
export function formatMissionStepLabel(key: string): string {
  const known = tryGetMissionStep(key)?.fullLabel;
  if (known) return known;
  const acronyms: Record<string, string> = {
    ff: "FF",
    ss: "SS",
    loas: "LOAs",
    loa: "LOA",
    tob: "ToB",
    ps: "PS",
    as: "AS",
    crm: "CRM",
  };
  return key
    .split("_")
    .map((word) => acronyms[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
